import { SolutionEnvironmentsEnum } from '@pypestream/api-services';

import { Pype } from '../xstate/prostudio.api-service.types';
import { type Channel, getChannelsFromPypes } from './get-channels-from-pypes';
import { PlatformConnection } from './get-pype-connections';

/** A channel pill that can deep-link to its channel-settings page. */
export interface PlatformChannel {
  name: string;
  channelId: string;
  /** Bot the channel starts chat with — resolved to a solution for routing. */
  botId?: string;
  type?: Channel['type'];
  /** Environment, for the pill's env dot. */
  env?: 'live' | 'sandbox';
}

/** A row in the Live Agent Platforms table — one `PypeConnection`. */
export interface LiveAgentPlatformRow {
  id: string;
  name: string;
  /** The connection's `agentRouting` (e.g. `salesforce`, or `external_agent`). */
  agentRouting: string;
  /** External connections' callback URL. */
  agentRoutingUrl?: string;
  /**
   * Key identifying the actual platform, for the Platform column's icon. Stored
   * directly on the connection as `platformId`, so no config parsing.
   */
  platformKey: string;
  /** Channels bound to this connection, resolved from `connection.pypes`. */
  channels: PlatformChannel[];
  /** When the connection itself last changed (ISO string). */
  lastUpdated?: string;
}

/** Maps a derived Channel to the slim pill shape used by the settings tables. */
export const toPlatformChannel = (channel: {
  name: string;
  id: string;
  botId?: string;
  type?: Channel['type'];
  env?: 'live' | 'sandbox';
}): PlatformChannel => ({
  name: channel.name,
  channelId: channel.id,
  botId: channel.botId,
  type: channel.type,
  env: channel.env,
});

/**
 * Display/option key for a platform. External integrations all persist as
 * `agent_routing: 'external_agent'` (the backend enum has no per-integration
 * external value) and share one callback URL, so the specific integration is
 * identified by its `integration_name` (e.g. `Quiq`, `Genesys`). Internal
 * platforms use their `agent_routing` directly. Falls back to the raw routing
 * value when the integration name isn't known yet.
 *
 * This is the key space stored as `PypeConnection.platformId`.
 */
export const resolvePlatformKey = (
  agentRouting: string | undefined,
  integrationName?: string | null
): string => {
  if (agentRouting === 'external_agent' && integrationName) {
    return integrationName;
  }
  return agentRouting ?? '';
};

/** `pype_connection_pype.env` is `Live`/`Sandbox`; channels use lowercase. */
export const toChannelEnv = (
  env: SolutionEnvironmentsEnum | string
): 'live' | 'sandbox' =>
  String(env).toLowerCase() === 'live' ? 'live' : 'sandbox';

/** The inverse: channel/UI env (`live`/`sandbox`) to the GraphQL enum. */
export const toSolutionEnvironment = (
  env: string | undefined
): SolutionEnvironmentsEnum =>
  String(env).toLowerCase() === 'live'
    ? SolutionEnvironmentsEnum.Live
    : SolutionEnvironmentsEnum.Sandbox;

/** Channel pills keyed by `${pypeId}:${env}` — see `indexChannelsByPype`. */
export type ChannelsByPype = Map<string, PlatformChannel[]>;

/**
 * Derives every channel in an environment ONCE, keyed for lookup by bound pype.
 *
 * `getChannelsFromPypes` walks pypes → streams → widgets and base64-decodes voice
 * metadata, so it is far too expensive to call per table row: projects run to a
 * thousand pypes. Callers build this once (memoized) and hand it to
 * `mapConnectionToRow` for each connection.
 *
 * A pype can back more than one channel, hence a list per key.
 */
export const indexChannelsByPype = (envPypes: Pype[]): ChannelsByPype => {
  const index: ChannelsByPype = new Map();

  for (const channel of getChannelsFromPypes(envPypes)) {
    const key = `${channel.pypeId}:${channel.env}`;
    const existing = index.get(key);
    if (existing) {
      existing.push(toPlatformChannel(channel));
    } else {
      index.set(key, [toPlatformChannel(channel)]);
    }
  }

  return index;
};

/**
 * Maps a connection to a table row.
 *
 * "Channels using it" comes straight from `connection.pypes` — the join rows the
 * backend maintains — and the index only supplies the names behind those pype
 * ids. A bound pype with no derivable channel (e.g. one with no streams yet) is
 * skipped rather than rendered as a blank pill.
 */
export const mapConnectionToRow = (
  connection: PlatformConnection,
  channelsByPype: ChannelsByPype = new Map()
): LiveAgentPlatformRow => ({
  id: connection.id,
  name: connection.name,
  agentRouting: connection.agentRouting,
  agentRoutingUrl: connection.agentRoutingUrl,
  platformKey: connection.platformId,
  channels: connection.pypes.flatMap(
    (pype) =>
      channelsByPype.get(`${pype.pypeId}:${toChannelEnv(pype.env)}`) ?? []
  ),
  lastUpdated: connection.updatedAt ?? connection.createdAt,
});
